import { Component } from 'react'
import type { ErrorInfo, ReactNode } from 'react'

export type PageItemErrorBoundaryProps = {
  /** Called once per caught error, with React's component stack. */
  onError: (error: Error, info: ErrorInfo) => void
  /** What stands in for the children while an error is held; `retry` clears it. */
  fallback: (error: Error, retry: () => void) => ReactNode
  children: ReactNode
}

type PageItemErrorBoundaryState = { error: Error | null }

/**
 * The net under one part of the editor. An error thrown while a subtree
 * renders, or inside one of its effects — a fit that never settles is the
 * one React itself throws, as "maximum update depth" — would otherwise
 * unmount the whole editor with it; caught here, the subtree gives way to
 * its fallback and everything around it stays as it was. A class, because
 * React hands errors only to a class's `getDerivedStateFromError`.
 */
export class PageItemErrorBoundary extends Component<
  PageItemErrorBoundaryProps,
  PageItemErrorBoundaryState
> {
  state: PageItemErrorBoundaryState = { error: null }

  static getDerivedStateFromError(error: Error): PageItemErrorBoundaryState {
    return { error }
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    this.props.onError(error, info)
  }

  retry = (): void => {
    this.setState({ error: null })
  }

  render(): ReactNode {
    const { error } = this.state
    if (error !== null) return this.props.fallback(error, this.retry)
    return this.props.children
  }
}

export default PageItemErrorBoundary
